import {until} from 'lit-html/directives/until';
import {repeat} from 'lit-html/directives/repeat';
import {gradesLabels} from '@modules/i18n-labels';
import '@modules/dropdown';
import {LoadingEllipses, LearnerActivity} from '@epg/epg-components';

const renderGenericItemsDropdown = (section, ctx, html) => {
  return html`
    <ems-dropdown class="dropdown d-block" ?disabled=${(!section.items || section.items?.length === 0)}>
      <button type="button"
              slot="dropdown-toggle" class="btn btn-outline-light w-100 justify-content-between align-items-center"
              data-bi-id="report_dropdown_${section.objectType}"
              ?disabled=${(!section.items || section.items?.length === 0)}>
        <span>${section.dropdownLabel}</span>
        <span class="ai ai-chevron-down"></span>
      </button>
      <div slot="dropdown-menu" class="dropdown-menu d-block">
        ${section.items?.map((item, itemIndex) => html`
          <button type="button"
              class="btn dropdown-item"
              data-bi-id="report_select_${section.objectType}_${item.value}"
              @click="${(e) => ctx.selectOption(item, {objectType: section.objectType}, itemIndex)}">${item.label}</button>
        `)}
      </div>
    </ems-dropdown>
  `;
};

const renderStudentDropdowns = (ctx, html) => {
  const section = {
    dropdownLabel: ctx.selected.student?.label ? ctx.selected.student.label : until(ctx.__('<tt-P4NABk0+>', '- All Students -')),
    items: ctx.students,
    objectType: 'students',
  };
  return html`
    <div class="mt-3">
      ${renderGenericItemsDropdown(section, ctx, html)}
    </div>
  `;
};

const renderTiersDropdowns = (section, ctx, html) => {
  return html`
    ${repeat(section.items, (groupsInTier) => groupsInTier, (groupsInTier, tierIndex) => {
      const selectedGroupInfo = ctx.selected.groups[tierIndex] || {label: until(ctx._r(ctx.__('<tt-RDMRn2xV>', '- Select a %r1% -'), ctx.tierLabels[tierIndex] || 'Class'))};
      return html`
        <ems-dropdown class="dropdown d-block ${tierIndex ? 'mt-3' : ''}">
          <button
            type="button"
            slot="dropdown-toggle"
            data-bi-id="report_dropdown_${section.objectType}"
            class="btn btn-outline-light w-100 justify-content-between align-items-center">
            <span>${selectedGroupInfo.label}</span>
            <span class="ai ai-chevron-down"></span>
          </button>
          <div slot="dropdown-menu" class="dropdown-menu d-block">
            ${repeat(groupsInTier, (group) => group, (group) => html`
              <button type="button"
                      class="btn dropdown-item"
                      data-bi-id="report_select_group_${group.value}"
                      @click="${(e) => ctx.selectGroup(group, tierIndex)}">${group.label}
              </button>`)}
          </div>
        </ems-dropdown>
      `;
    })}
    ${ctx.showGrades ? renderGradeDropdown(ctx, html) : ''}
    ${renderStudentDropdowns(ctx, html)}
  `;
};

const renderGradeDropdown = (ctx, html) => {
  return html`
  <ems-dropdown class="dropdown d-block mt-3">
    <button
      type="button"
      slot="dropdown-toggle"
      data-bi-id="report_dropdown_grade"
      class="btn btn-outline-light w-100 justify-content-between align-items-center">
      <span>${ctx.selected.grade !== '' ? until(gradesLabels[ctx.selected.grade]) : until(ctx.__('<tt-gQKt3MTQ>', 'Grade'))}</span>
      <span class="ai ai-chevron-down"></span>
    </button>
    <div slot="dropdown-menu" class="dropdown-menu d-block">
      ${repeat(ctx.grades, (grade) => grade, (grade) => html`
        <button type="button"
                class="btn dropdown-item"
                data-bi-id="report_select_group_${grade}"
                @click="${(e) => ctx.setGrade(grade)}">${until(gradesLabels[grade])}
        </button>`)}
    </div>
  </ems-dropdown>
`;
};

const renderReportStep = (section, ctx, html) => {
  const sectionMap = {
    tiers: {
      step: 1,
      label: ctx.__('<tt-ZBT8nbIX>', 'Select a Tier'),
      items: ctx.tiers,
      objectType: 'groups',
      renderDropdown: renderTiersDropdowns
    },
    reportType: {
      step: 2,
      label: ctx.__('<tt-xwsCvWej>', 'Select a Report Type'),
      dropdownLabel: ctx.selected.reportType?.label ? ctx.selected.reportType.label : until(ctx.__('<tt-cUb2y4/e>', '- Select a Report Type -')),
      items: ctx.reportTypes,
      objectType: 'report_types',
      renderDropdown: renderGenericItemsDropdown
    },
  };
  const sectionConfig = sectionMap[section];
  return html`
    <div class="step-wrapper ${ctx.timeFrameDisabled && section === 'date' ? 'dropdown-hidden' : ''}">
     <p>${ctx.timeFrameDisabled && section === 'date' ? 'dropdown-hidden' : ''}</p>
      <div class="d-flex flex-row align-items-center">
        <div class="rounded-circle bg-primary text-white text-center">${sectionConfig.step}</div>
        <span class="h5 mb-0 font-weight-bold">${until(sectionConfig.label)}</span>
      </div>
      <div class="step-dropdown-wrapper">
        ${sectionConfig.renderDropdown(sectionConfig, ctx, html)}
      </div>
    </div>
  `;
};

const renderReportInfo = (ctx, html) => {
  if (!ctx.reportDate && ctx.reportDatesByMonth.length === 0) {
    return;
  }

  let reportInfo = '';

  if (ctx.reportDatesByMonth.length > 0) {
    reportInfo = html`
      ${repeat(ctx.reportDatesByMonth, (date) => html`<div class="line-height-1">[${date}]</div>`)}
    `;
  } else if (ctx.reportDate !== '') {
    reportInfo = html `<div class="line-height-1">[${ctx.reportDate}]</div>`;
  } else {
    return;
  }

  return html`
    <span class="bg-info rounded d-flex flex-row mb-3 align-items-center py-3">
      <div class="ai ai-info mx-4"></div>
      <div class="d-flex flex-column font-weight-semibold">
        <div class="line-height-1">${until(ctx.__('<tt-bZ81RnQr>', 'Report last generated'))}</div>
        ${reportInfo}
      </div>
    </span>
  `;
};

const renderDownloadButton = (reportType, ctx, html) => {
  return html`
    <button
      @click="${ctx.viewReport}"
      class="btn btn-primary rounded-pill d-block" ?disabled=${ctx.downloadDisabled}
      data-bi-id="download_report">
      ${ctx.loading ?
        html`<loading-ellipses />` :
        getDownloadButton(ctx, html)}
    </button>
  `;
};

const renderCsvButton = (ctx, html) => {
  return html`
    <button
      @click="${ctx.downloadCsv}"
      class="btn btn-primary rounded-pill d-block csv-btn ${ctx.csvDisabled ? 'dropdown-hidden' : ''}"
      data-bi-id="download_report">
      ${ctx.loading ?
        html`<loading-ellipses />` :
        getCsvButton(ctx, html)}
    </button>
    ${(ctx.showCsvMissingText && !ctx.csvDisabled) ? html`<p class="csv-text-red csv-text">${until(ctx.__('<tt-FJsreDim>', 'Placements have not been completed. No CSV is available.'))}</p>` : ''}
  `;
};

const getCsvButton = (ctx, html) => {
  return html`
      <span class="align-middle">${until(ctx.__('<tt-n+9eMVXi>', 'Download CSV'))}</span>
    `;
};

const getDownloadButton = (ctx, html) => {
  return html`
          <span class="align-middle">${until(ctx.__('<tt-HaIussSQ>', 'View Report'))}</span>
        `;
};

const renderLeftSide = (ctx, html) => {
  return html`
    <div class="h3 font-weight-bold text-center mb-4">${until(ctx.__('<tt-kj90Nmdt>', 'Placement & Growth Reports'))}</div>
    <p class="text-left font-size-small w-100 m-auto">${until(ctx.__('<tt-mmLoP37V>', 'Reports for Kindergarten through 5th grade students will be ready within 24 hours after their placement assessment is finished.'))}</p>
    <div class="dropdown-wrapper mx-auto">
      ${renderReportStep('tiers', ctx, html)}
      ${renderReportStep('reportType', ctx, html)}
      ${renderDownloadButton(ctx.selected?.reportType?.value, ctx, html)}
      ${renderCsvButton(ctx, html)}
    </div>
  `;
};

const renderRightSide = (ctx, html) => {
  return html`
    <learner-activity></learner-activity>
  `;
};


const renderBody = (ctx, html) => {
  return html`
    <div class="row">
      <div class="col mx-2 border-solid">${renderLeftSide(ctx, html)}</div>
      <div class="col mx-2">${renderRightSide(ctx, html)}</div>
    </div>
  `;
};

export default (ctx, html) => html`
  <ems-modal
    id="print_reports_modal"
    size="modal-lg"
    @on-close="${ctx.closeModal}">
    <div slot="header">
      <div class="h2 font-weight-bold text-center mb-4">${until(ctx.__('<tt-W2m3rTZi>', 'Reports Center'))}</div>
    </div>
    <div slot="body">
      ${renderBody(ctx, html)}
    </div>
  </ems-modal>
`;
